'use client';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb, GlobalLoading } from '@/shared/components';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import ElementSearchIcon from '@/assets/images/pdp/periodic-table.svg';
import StructureSearchIcon from '@/assets/images/pdp/structure_search_icon_bold.svg';
import Image from 'next/image';
import { ProductAttributes } from './components/product-attributes';
import { ProductDescription } from './components/product-description';
import { ProductSpecifications } from './components/product-specifications';
import { RecommendedProducts } from './components/recommended-products';
import {
  useQueryHoldCode,
  useQueryProductDetail,
  useQuerySpecificationSheet,
  useQueryRecommendedProducts,
} from '@/hooks/product-detail.hook';
import { useParams, useSearchParams } from 'next/navigation';
import { useProductDetailStore } from './store';
import { DocumentDownload } from './components/document-download';
import HoldCodeMessage from '@/shared/pageComponents/hold-code-message';
import { useQueryCatalogNotes, useQueryChildCatalogs } from '@/hooks/search-result.hook';
import LanguageContext from '@/context/language';
import Link from 'next/link';
import { COUNTRY, DOMAIN } from '@/app/utils/constant';
import { usePageTracking, useEventTracking } from '@/shared/tracking/use-tracking';
import { AddCartFailMessage } from '@/shared/pageComponents/add-cart-fail-message';
import { usePdpPayloadTracking } from '@/shared/tracking/use-tracking-helpers';
import { pdpInteractions } from '@/shared/tracking/page-load';
import { PriceErrorMessage } from '@/shared/pageComponents/price-error-message';
import { decodePdpLink } from '@/app/utils/build-pdp-link';
import { useCountry } from '@/hooks/use-country';
import { PdpRedirectType } from '@/types/search-result';

export default function ProductDetailPage() {
  const { t } = useLang(NAMESPACE.pdp);
  const { lang } = useContext(LanguageContext);
  const params = useParams<{
    catalogNumber: string;
  }>();
  const searchParams = useSearchParams();
  const { isCN } = useCountry();
  const catalogNumber = decodePdpLink(params.catalogNumber);
  const productDetailStore = useProductDetailStore();
  const { data, isLoading, isError } = useQueryProductDetail(catalogNumber);
  const { data: specificationSheet } = useQuerySpecificationSheet(
    data?.data?.productBaseInfo?.catalogNumber,
    data?.data?.productBaseInfo?.globalRootSku,
    !!data?.data?.productBaseInfo?.catalogNumber && !!data?.data?.productBaseInfo?.catalogNumber.split('.')[0]
  );
  const { data: childCatalogs, isLoading: childCatalogLoading } = useQueryChildCatalogs(catalogNumber);
  const childCatalogNumbers = useMemo(() => {
    return childCatalogs?.map((item) => item.childCatalogNumber) || [];
  }, [childCatalogs]);

  const {
    data: childCatalogData,
    isLoading: noteLoading,
    isFetching: noteFetching,
  } = useQueryCatalogNotes(childCatalogNumbers);

  // AA tracking starts
  const { trackEvent } = useEventTracking();
  // page tracking for recommended products and child skus
  const { data: recommendedData, isFetching: recommendedFetching } = useQueryRecommendedProducts(
    data?.data?.productBaseInfo?.globalRootSku
  );
  const dataArray = usePdpPayloadTracking(
    data,
    { data: recommendedData, isFetching: recommendedFetching },
    {
      data: childCatalogData,
      isFetching: noteFetching,
    }
  );
  usePageTracking(
    {
      breadcrumb: new Array(4).fill(catalogNumber),
      items: dataArray,
    },
    dataArray && dataArray.length > 0 ? true : false
  );

  useEffect(() => {
    if (!isLoading && !isError && data) {
      GlobalLoading.hide();
      productDetailStore.setProductDetail(data.data);
    } else {
      GlobalLoading.show();
    }
  }, [isLoading, isError, data]);

  const { data: holdCodeResponse } = useQueryHoldCode(catalogNumber);

  const elementSearchUrl = `${DOMAIN}/${COUNTRY}/${lang}/home/chemicals/interactive-periodic-table-elements.html`;
  const clickEvent = (clicktype) => {
    trackEvent({
      key: 'eventsData',
      action: 'PDPInteraction',
      payload: {
        event_name: 'PDPInteraction',
        PDP_interaction_type: clicktype,
      },
    });
  };

  useEffect(() => {
    return () => {
      productDetailStore.setBatchAddFailMessage([]);
      productDetailStore.setFaliPriceSkus([]);
    };
  }, []);

  const productDetailBreadcrumbs = useMemo(() => {
    const breadcrumbs = productDetailStore.productDetail?.breadcrumbs || [];

    if (breadcrumbs.length === 0) return [];

    let productTitle = productDetailStore.productDetail?.productBaseInfo?.productTitle;
    if (isCN && lang === 'en') {
      productTitle = productDetailStore.productDetail?.productBaseInfo?.enProductTitle;
    }

    const productTitleItem = {
      name: (
        <span
          dangerouslySetInnerHTML={{
            __html: productTitle,
          }}
        ></span>
      ),
    };
    if (breadcrumbs.length) {
      return [
        ...breadcrumbs.map((item) => {
          return {
            ...item,
            url: `/apac/search/category/${item.id}`,
          };
        }),
        productTitleItem,
      ];
    }

    return [productTitleItem];
  }, [productDetailStore.productDetail]);

  const skuStatusFromSearchPage = useMemo(() => {
    const skuFromSearch = searchParams.get('skuFromSearch') || '';
    const typeFromSearch = searchParams.get('typeFromSearch') as PdpRedirectType;
    if (!skuFromSearch || !typeFromSearch) return null;
    return {
      sku: decodeURIComponent(skuFromSearch),
      type: typeFromSearch,
    };
  }, [searchParams]);

  return (
    <>
      {productDetailStore.productDetail && (
        <>
          <div className="w-full grid grid-cols-[1fr_auto] gap-x-4 justify-between items-center sm:justify-start sm:grid-cols-1 sm:gap-y-2">
            <Breadcrumb items={productDetailBreadcrumbs} className="pdp-breadcrumb" />
            <div className="flex gap-x-4 sm:py-1">
              <Link
                prefetch
                className="p-0 text-left flex items-center gap-x-2 pdp-element-search"
                href={elementSearchUrl}
                onClick={() => clickEvent(pdpInteractions['element_search'])}
              >
                <Image src={ElementSearchIcon} alt="" />
                <span>{t('element.search')}</span>
              </Link>
              <Link
                className="p-0 text-left flex items-center gap-x-2 pdp-structure-search"
                href="/search/structure"
                onClick={() => clickEvent(pdpInteractions['structure_search'])}
              >
                <Image src={StructureSearchIcon} alt="" />
                <span>{t('structure.search')}</span>
              </Link>
            </div>
          </div>
          <div className="flex flex-col gap-y-1 print:hidden mt-5">
            <Alert
              open={productDetailStore.productDetail.productBaseInfo?.dwgFlag === 'Y'}
              type={'warning'}
              white
              className="print:hidden w-full self-start"
              title={'Warning:'}
              content={t('dwg.tips')}
            />
            <Alert
              open={skuStatusFromSearchPage && skuStatusFromSearchPage?.type === 'DBC'}
              type={'warning'}
              white
              content={t('discounited.sku.tooltip', { sku: skuStatusFromSearchPage?.sku })}
            />
            <Alert
              open={skuStatusFromSearchPage && skuStatusFromSearchPage?.type === 'DEC'}
              type={'warning'}
              white
              content={t('not.web.orderable.tooltip', { sku: skuStatusFromSearchPage?.sku })}
            />
            <AddCartFailMessage
              failList={productDetailStore.batchAddFailMessage}
              onClose={() => productDetailStore.setBatchAddFailMessage([])}
            />
            <HoldCodeMessage holdCodes={holdCodeResponse?.data} />
            <PriceErrorMessage
              failSkus={productDetailStore.faliPriceSkus}
              onClose={() => productDetailStore.setFaliPriceSkus([])}
            />
          </div>
          {childCatalogs && (
            <ProductSpecifications
              childCatalogs={childCatalogs}
              childCatalogNumbers={childCatalogNumbers}
              childCatalogData={childCatalogData}
              childCatalogLoading={childCatalogLoading}
              specificationSheetUrl={specificationSheet?.data || ''}
              noteLoading={noteLoading}
            />
          )}
          <ProductAttributes className="mt-8" />
          <div className="w-full h-[1px] bg-gray-400 mt-[60px] mb-6" />
          <ProductDescription />
          {productDetailStore.productDetail.productBaseInfo.globalRootSku && (
            <RecommendedProducts
              className="mt-16 recommend-products"
              catalogNumber={productDetailStore.productDetail.productBaseInfo.globalRootSku}
            />
          )}
          {productDetailStore.productDetail?.productBaseInfo && (
            <DocumentDownload
              className="mt-[75px] w-4/5 sm:w-full md:w-full"
              childSku={childCatalogNumbers}
              rootSku={productDetailStore.productDetail.productBaseInfo?.globalRootSku}
              brandName={productDetailStore.productDetail.productBaseInfo?.brandImage}
              id="pdp_documents_downloads"
              childCatalogNumbers={childCatalogNumbers}
            />
          )}
        </>
      )}
    </>
  );
}
